/**
 * journalDispatch — pure decision module for the JournalScene (B-010).
 *
 * Phaser-free. The scene takes a raw `JournalSnapshot` (or `null` while the
 * server reply is in flight) and a tab id; this module returns a
 * fully-validated, render-ready tab-state. The renderer in `JournalScene`
 * blindly trusts the dispatch result, so this is the *only* place that
 * decides what each tab says, including empty-state copy.
 *
 * Mirrors the contract of `vocationFeedbackDispatch.ts`: zero runtime
 * dependencies beyond `@game/shared` types, easy to unit-test in the
 * Node-only vitest harness.
 *
 * Canonical references:
 *   - `docs/gameplay-systems-backlog-2026-04-25.md` B-010
 *   - `docs/design/whispers-seed.md`
 *   - `packages/shared/src/journal/types.ts`
 */

import type {
  CraneInboxEntry,
  JournalSnapshot,
  PetJournalEntry,
  Relic,
  Rumor,
  Whisper,
} from '@game/shared';
import {
  COOKING_RECIPES,
  type CookingRecipeDefinition,
  ItemType,
  VocationKey,
} from '@game/shared';

/** Stable tab identifiers. Serializable so a future remember-last-tab
 *  registry key can persist user choice across sessions. */
export const JOURNAL_TABS = [
  'whispers',
  'relics',
  'recipes',
  'pet',
  'visitors',
  'home-stage',
  'rumors',
  'expedition-log',
] as const;

export type JournalTabId = (typeof JOURNAL_TABS)[number];

/** Display title for a tab — kept here so the scene chrome and any
 *  future deep-link badge agree on the same string. */
export const JOURNAL_TAB_TITLES: Readonly<Record<JournalTabId, string>> = {
  'whispers': 'Whispers',
  'relics': 'Relics',
  'recipes': 'Recipes',
  'pet': 'Pet',
  'visitors': 'Visitors',
  'home-stage': 'Home',
  'rumors': 'Rumors',
  'expedition-log': 'Expeditions',
};

/** A single line rendered in a tab's scrollable list. The scene draws each
 *  line as a heading + body block; the heading is optional. */
export interface JournalLine {
  /** Stable id for this entry — used as a Phaser-side key for line objects
   *  so re-renders can diff cheaply. */
  id: string;
  /** Optional heading (e.g. a Whisper's source/realm tag, a Rumor's name). */
  heading?: string;
  /** Body prose. Always a non-empty string. */
  body: string;
  /** Optional muted footnote (e.g. "unread", "1 day ago"). */
  footnote?: string;
  /** Epoch-ms timestamp when this entry was received/unlocked. Used by
   *  the WhispersTab renderer to show "date dreamed". */
  unlockedAt?: number;
}

/**
 * Render-ready tab state. The scene renders `lines` top-to-bottom in the
 * scroll area; `emptyMessage` is shown when `lines` is empty (so the tab
 * never looks broken — Ghibli-soft empty states are part of the UX).
 */
export interface JournalTabState {
  tabId: JournalTabId;
  title: string;
  lines: JournalLine[];
  /** Shown when `lines` is empty. Always a non-empty string. */
  emptyMessage: string;
  /** Convenience: count of unread items on this tab. Whispers tab only;
   *  every other tab returns 0. */
  unreadCount: number;
}

/**
 * Build the tab state for the given tab id from a snapshot. When the
 * snapshot is `null` (request in flight, or no JournalLedger wired
 * server-side), we return the empty-state copy — the scene renders a calm
 * placeholder rather than a spinner.
 */
export function buildJournalTabState(
  tabId: JournalTabId,
  snapshot: JournalSnapshot | null,
  recipesContext?: RecipesTabContext,
): JournalTabState {
  switch (tabId) {
    case 'whispers':
      return buildWhispersTab(snapshot);
    case 'relics':
      return buildRelicsTab(snapshot);
    case 'recipes':
      return buildRecipesTab(recipesContext);
    case 'pet':
      return buildPetTab(snapshot);
    case 'visitors':
      return buildVisitorsTab(snapshot);
    case 'home-stage':
      return buildHomeStageTab();
    case 'rumors':
      return buildRumorsTab(snapshot);
    case 'expedition-log':
      return buildExpeditionLogTab();
  }
}

/** Context needed by the Recipes tab to decide discovered/undiscovered
 *  status and Fool-vocation poetic hints. */
export interface RecipesTabContext {
  /** Set of recipe ids the player has successfully cooked this session. */
  discoveredRecipeIds: ReadonlySet<string>;
  /** Player's vocation key (null if unknown). Fool gets poetic hints. */
  playerVocation: VocationKey | null;
}

/** A client-local journal entry injected by non-server systems (e.g.
 *  festival lifecycle toasts). Merged into the Whispers tab. */
export interface LocalJournalEntry {
  id: string;
  heading: string;
  body: string;
  timestamp: number;
}

/**
 * Build all tab states in one pass. The scene calls this once per
 * snapshot refresh and caches the result; tab-switch is then a pure
 * lookup, no recompute.
 *
 * `localEntries` — optional client-side entries (e.g. festival events)
 * appended to the Whispers tab. Sorted newest-first alongside server whispers.
 */
export function buildAllJournalTabs(
  snapshot: JournalSnapshot | null,
  localEntries?: LocalJournalEntry[],
  recipesContext?: RecipesTabContext,
): Record<JournalTabId, JournalTabState> {
  return {
    'whispers': buildWhispersTab(snapshot, localEntries),
    'relics': buildRelicsTab(snapshot),
    'recipes': buildRecipesTab(recipesContext),
    'pet': buildJournalTabState('pet', snapshot),
    'visitors': buildVisitorsTab(snapshot),
    'home-stage': buildJournalTabState('home-stage', snapshot),
    'rumors': buildJournalTabState('rumors', snapshot),
    'expedition-log': buildExpeditionLogTab(),
  };
}

// ── Per-tab builders ──────────────────────────────────────────────────────

function buildWhispersTab(
  snapshot: JournalSnapshot | null,
  localEntries?: LocalJournalEntry[],
): JournalTabState {
  const whispers = snapshot?.whispers ?? [];
  const readMap = snapshot?.readAtByWhisperId ?? {};
  // Newest first — players just got these; the journal "page" reads down
  // from the most recent dream.
  const sorted = [...whispers].sort((a, b) => b.unlockedAt - a.unlockedAt);
  const lines: JournalLine[] = sorted.map((w) => whisperLine(w, readMap));

  // Merge client-local entries (e.g. festival events) — AGE-1199
  if (localEntries && localEntries.length > 0) {
    const localLines: JournalLine[] = localEntries
      .sort((a, b) => b.timestamp - a.timestamp)
      .map((e) => ({ id: e.id, heading: e.heading, body: e.body }));
    // Interleave: local entries go at the top since they are recent events
    lines.unshift(...localLines);
  }

  const unreadCount = snapshot?.unreadCount ?? 0;
  return {
    tabId: 'whispers',
    title: JOURNAL_TAB_TITLES['whispers'],
    lines,
    emptyMessage:
      'No Whispers yet. Sleep at a hearth or shrine to dream.',
    unreadCount,
  };
}

function whisperLine(
  whisper: Whisper,
  readMap: Record<string, number>,
): JournalLine {
  const heading = formatWhisperHeading(whisper);
  const readAt = readMap[whisper.id] ?? 0;
  const unread = readAt <= 0;
  return {
    id: whisper.id,
    heading,
    body: whisper.body,
    footnote: unread ? 'unread' : undefined,
    unlockedAt: whisper.unlockedAt,
  };
}

/** Convert a Whisper's source/realm tags into a quiet display heading. */
export function formatWhisperHeading(whisper: Whisper): string {
  const sourceLabel = formatWhisperSource(whisper.source);
  const realmLabel = whisper.realm === 'any' ? '' : ` · ${formatRealm(whisper.realm)}`;
  return `${sourceLabel}${realmLabel}`;
}

function formatWhisperSource(source: Whisper['source']): string {
  switch (source) {
    case 'dreamed':
      return 'Dreamed';
    case 'shrine':
      return 'At the shrine';
    case 'expedition':
      return 'On the road';
    case 'world-event':
      return 'Of the world';
    case 'rumor':
      return 'Whispered';
    case 'earned':
      return 'Earned';
    case 'festival':
      return 'Festival';
  }
}

function formatRealm(realm: Whisper['realm']): string {
  switch (realm) {
    case 'verdana':
      return 'Verdana';
    case 'sablewind':
      return 'Sablewind';
    case 'frostmere':
      return 'Frostmere';
    case 'embermire':
      return 'Embermire';
    case 'any':
      return '';
  }
}

function buildPetTab(snapshot: JournalSnapshot | null): JournalTabState {
  const pet = snapshot?.pet ?? null;
  if (!pet) {
    return {
      tabId: 'pet',
      title: JOURNAL_TAB_TITLES['pet'],
      lines: [],
      emptyMessage:
        'No companion has come yet. They arrive in their own time.',
      unreadCount: 0,
    };
  }

  const lines: JournalLine[] = [];

  if (!pet.hatched) {
    // Pre-hatch: show bonding cue as a quiet line.
    if (pet.bondingCue) {
      lines.push({
        id: 'pet-bonding-cue',
        body: pet.bondingCue,
      });
    }
    return {
      tabId: 'pet',
      title: JOURNAL_TAB_TITLES['pet'],
      lines,
      emptyMessage:
        'No companion has come yet. They arrive in their own time.',
      unreadCount: 0,
    };
  }

  // Hatched pet — build full journal entry.
  const formName = pet.formId
    ? formatFormName(pet.formId)
    : 'Companion';

  // Line 1: Form heading + concept body.
  if (pet.formConcept) {
    lines.push({
      id: 'pet-form',
      heading: formName,
      body: pet.formConcept,
      footnote: pet.formMoodTag ?? undefined,
    });
  }

  // Line 2: Personality blurb from surface adjectives.
  if (pet.surfaceAdjectives.length > 0) {
    const adjectiveList = pet.surfaceAdjectives.join(', ');
    lines.push({
      id: 'pet-personality',
      body: `They seem ${adjectiveList}.`,
    });
  }

  // Line 3+: Active behaviors as milestones.
  for (let i = 0; i < pet.activeBehaviors.length; i++) {
    lines.push({
      id: `pet-behavior-${i}`,
      body: pet.activeBehaviors[i],
      footnote: 'observed',
    });
  }

  return {
    tabId: 'pet',
    title: JOURNAL_TAB_TITLES['pet'],
    lines,
    emptyMessage:
      'No companion has come yet. They arrive in their own time.',
    unreadCount: 0,
  };
}

/** Format a form id like 'moss-kodama' into 'Moss Kodama'. */
function formatFormName(formId: string): string {
  return formId
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function buildVisitorsTab(snapshot: JournalSnapshot | null): JournalTabState {
  const cranes = snapshot?.craneInbox ?? [];
  const sorted = [...cranes].sort((a, b) => b.deliveredAt - a.deliveredAt);
  const lines: JournalLine[] = sorted.map((c) => craneLine(c));
  const unreadCount = sorted.filter((c) => c.readAt === null).length;
  return {
    tabId: 'visitors',
    title: JOURNAL_TAB_TITLES['visitors'],
    lines,
    emptyMessage:
      'No visitors yet. Leave the door unlatched, and someone will pass.',
    unreadCount,
  };
}

function craneLine(crane: CraneInboxEntry): JournalLine {
  const heading = crane.senderName !== 'Unknown'
    ? `Paper crane from ${crane.senderName}`
    : 'Paper crane from a stranger';
  const footnote = crane.readAt === null ? 'unread' : undefined;
  return {
    id: crane.craneId,
    heading,
    body: crane.message || 'The crane carried no message — only the act of sending.',
    footnote,
  };
}

function buildHomeStageTab(): JournalTabState {
  // Home stage is server-owned; the client journal does not yet read it.
  // When a HomeStage projection lands on the schema, this tab will render
  // the current stage label + a quiet line about what comes next.
  return {
    tabId: 'home-stage',
    title: JOURNAL_TAB_TITLES['home-stage'],
    lines: [],
    emptyMessage:
      'Your home is taking its first shape. Tend it slowly; you have time.',
    unreadCount: 0,
  };
}

function buildRumorsTab(snapshot: JournalSnapshot | null): JournalTabState {
  const rumors = snapshot?.rumors ?? [];
  const sorted = [...rumors].sort((a, b) => b.unlockedAt - a.unlockedAt);
  const lines: JournalLine[] = sorted.map((r) => rumorLine(r));
  return {
    tabId: 'rumors',
    title: JOURNAL_TAB_TITLES['rumors'],
    lines,
    emptyMessage:
      'No rumors yet. They gather where whispers cross paths.',
    unreadCount: 0,
  };
}

function rumorLine(rumor: Rumor): JournalLine {
  return {
    id: rumor.id,
    heading: rumor.name,
    body: rumor.hint,
  };
}

function buildRelicsTab(snapshot: JournalSnapshot | null): JournalTabState {
  const relics = snapshot?.relics ?? [];
  const sorted = [...relics].sort((a, b) => b.acquiredAt - a.acquiredAt);
  const lines: JournalLine[] = sorted.map((r) => relicLine(r));
  return {
    tabId: 'relics',
    title: JOURNAL_TAB_TITLES['relics'],
    lines,
    emptyMessage:
      'No relics yet. They are found in deep places, left by those who came before.',
    unreadCount: 0,
  };
}

function relicLine(relic: Relic): JournalLine {
  const tierLabel = relic.tier.charAt(0).toUpperCase() + relic.tier.slice(1);
  return {
    id: relic.id,
    heading: relic.name,
    body: relic.flavor,
    footnote: tierLabel,
  };
}

// ── Ingredient display names (recipe tab) ────────────────────────────────

const INGREDIENT_DISPLAY_NAMES: Partial<Record<ItemType, string>> = {
  [ItemType.MaterialWood]: 'Wood',
  [ItemType.MaterialStone]: 'Stone',
  [ItemType.MaterialThatch]: 'Thatch',
  [ItemType.MaterialCloth]: 'Cloth',
  [ItemType.MaterialInk]: 'Ink',
  [ItemType.MaterialWax]: 'Wax',
  [ItemType.MaterialAsh]: 'Ash',
  [ItemType.MaterialThawedWater]: 'Thawed Water',
  [ItemType.MaterialEmber]: 'Ember',
  [ItemType.MaterialSpiritsong]: 'Spiritsong',
};

/** Poetic Fool hints for undiscovered recipes. Keyed by recipe id. */
const FOOL_RECIPE_HINTS: Record<string, string> = {
  recipe_bread: 'The simplest things come from what the field remembers.',
  recipe_stew_verdana: 'Where the forest meets water, something thick waits.',
  recipe_stew_sablewind: 'Dark things settle when fire meets the mountain.',
  recipe_stew_frostmere: 'Wrap what you have; the cold will do the rest.',
  recipe_stew_embermire: 'Even ash has warmth left to give, if you wait.',
  recipe_tea: 'Seal the leaves before the steam escapes.',
  recipe_feast: 'A table set for many fills more than one.',
  recipe_pet_dish: 'Something small, made with care, for a friend.',
};

function formatIngredientLine(recipe: CookingRecipeDefinition): string {
  const parts = recipe.ingredients.map((ing) => {
    const name = INGREDIENT_DISPLAY_NAMES[ing.itemType] ?? ing.itemType;
    return ing.quantity > 1 ? `${ing.quantity} ${name}` : name;
  });
  return parts.join(' + ');
}

function buildRecipesTab(context?: RecipesTabContext): JournalTabState {
  const discovered = context?.discoveredRecipeIds ?? new Set<string>();
  const vocation = context?.playerVocation ?? null;
  const isFool = vocation === VocationKey.Fool;

  // Sort: discovered first, then undiscovered. Within each group, keep catalog order.
  const sorted = [...COOKING_RECIPES].sort((a, b) => {
    const aDisc = discovered.has(a.id) ? 0 : 1;
    const bDisc = discovered.has(b.id) ? 0 : 1;
    return aDisc - bDisc;
  });

  const lines: JournalLine[] = sorted.map((recipe) => {
    const isDiscovered = discovered.has(recipe.id);

    if (isDiscovered) {
      const ingredientLine = formatIngredientLine(recipe);
      return {
        id: recipe.id,
        heading: recipe.name,
        body: `${ingredientLine}  ->  ${recipe.name}`,
        footnote: recipe.description,
      };
    }

    // Undiscovered
    const body = isFool && FOOL_RECIPE_HINTS[recipe.id]
      ? FOOL_RECIPE_HINTS[recipe.id]
      : '???';
    return {
      id: recipe.id,
      heading: '???',
      body,
      footnote: 'undiscovered',
    };
  });

  return {
    tabId: 'recipes',
    title: JOURNAL_TAB_TITLES['recipes'],
    lines,
    emptyMessage:
      'No recipes yet. Cook at the hearth to discover what can be made.',
    unreadCount: 0,
  };
}

function buildExpeditionLogTab(): JournalTabState {
  return {
    tabId: 'expedition-log',
    title: JOURNAL_TAB_TITLES['expedition-log'],
    lines: [],
    emptyMessage:
      'No expeditions recorded yet. The road remembers, even when you forget.',
    unreadCount: 0,
  };
}

// ── Snapshot validation ───────────────────────────────────────────────────

/**
 * Validate + narrow an incoming `MSG_JOURNAL_SNAPSHOT` payload. Returns
 * `null` for any structural issue. The scene treats `null` as "drop the
 * payload silently and keep rendering the current cache."
 */
export function dispatchJournalSnapshot(
  payload: unknown,
): JournalSnapshot | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Partial<JournalSnapshot>;
  if (typeof p.characterId !== 'string') return null;
  if (!Array.isArray(p.whispers)) return null;
  if (!Array.isArray(p.rumors)) return null;
  if (!p.readAtByWhisperId || typeof p.readAtByWhisperId !== 'object') return null;
  if (typeof p.unreadCount !== 'number') return null;
  return {
    characterId: p.characterId,
    whispers: p.whispers as Whisper[],
    rumors: p.rumors as Rumor[],
    relics: Array.isArray(p.relics) ? (p.relics as Relic[]) : undefined,
    pet: validatePetEntry(p.pet),
    craneInbox: Array.isArray(p.craneInbox) ? (p.craneInbox as CraneInboxEntry[]) : undefined,
    readAtByWhisperId: p.readAtByWhisperId as Record<string, number>,
    unreadCount: p.unreadCount,
    discoveredRecipeIds: Array.isArray(p.discoveredRecipeIds) ? (p.discoveredRecipeIds as string[]) : undefined,
    vocationKey: typeof p.vocationKey === 'string' ? p.vocationKey : undefined,
  };
}

/** Validate an optional pet journal entry from the wire. */
function validatePetEntry(raw: unknown): PetJournalEntry | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const p = raw as Partial<PetJournalEntry>;
  if (typeof p.hatched !== 'boolean') return undefined;
  return {
    hatched: p.hatched,
    bondingCue: typeof p.bondingCue === 'string' ? p.bondingCue : null,
    formId: typeof p.formId === 'string' ? p.formId : null,
    formConcept: typeof p.formConcept === 'string' ? p.formConcept : null,
    formMoodTag: typeof p.formMoodTag === 'string' ? p.formMoodTag : null,
    surfaceAdjectives: Array.isArray(p.surfaceAdjectives) ? p.surfaceAdjectives : [],
    activeBehaviors: Array.isArray(p.activeBehaviors) ? p.activeBehaviors : [],
    hatchedAt: typeof p.hatchedAt === 'number' ? p.hatchedAt : null,
  };
}
